<template>
  <div class="ws-dash">
  

    <CAlert v-if="errorMessage" color="danger" show>{{ errorMessage }}</CAlert>

    <section class="ws-stats">
      <router-link
        v-for="card in cards"
        :key="card.key"
        :to="card.to"
        class="ws-stat"
        :style="{ background: card.gradient }"
      >
        <span class="ws-stat__icon" v-html="card.icon"></span>
        <span class="ws-stat__count">
          <CSpinner v-if="loading" size="sm" />
          <template v-else>{{ animated[card.key] }}</template>
        </span>
        <span class="ws-stat__label">{{ card.label }}</span>
        <span class="ws-stat__hint">{{ card.hint }}</span>
        <span class="ws-stat__arrow" aria-hidden="true">↗</span>
      </router-link>
    </section>

    <section class="ws-quick">
      <header class="ws-quick__head">
        <h3>Quick Actions</h3>
        <p>Jump into common management tasks.</p>
      </header>
      <div class="ws-quick__grid">
        <router-link
          v-for="action in quickActions"
          :key="action.key"
          :to="action.to"
          class="ws-quick__card"
          :class="`ws-quick__card--${action.tone}`"
        >
          <span class="ws-quick__title">{{ action.title }}</span>
          <span class="ws-quick__desc">{{ action.desc }}</span>
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const SVG_BASE = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"'

const ICONS = {
  // Pill/capsule + leaf — products (drugs + agro)
  bag:     `<svg ${SVG_BASE}><path d="M10.5 3.5a4.5 4.5 0 0 1 6.4 6.4l-7 7a4.5 4.5 0 1 1-6.4-6.4l7-7Z"/><path d="M7 7l10 10"/><path d="M19 14c1.5 0 3 1 3 3s-1.5 4-4 4"/></svg>`,
  // Newspaper — articles
  article: `<svg ${SVG_BASE}><path d="M4 5h13a2 2 0 0 1 2 2v11a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5Z"/><path d="M19 8h2v9a2 2 0 0 1-2 2"/><path d="M8 9h7M8 13h7M8 17h4"/></svg>`,
  // Megaphone — news
  news:    `<svg ${SVG_BASE}><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Z"/><path d="M14 8a5 5 0 0 1 0 8"/><path d="M17 5a8 8 0 0 1 0 14"/></svg>`,
  // Building — divisions
  org:     `<svg ${SVG_BASE}><rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2"/><path d="M10 21v-3h4v3"/></svg>`,
  // Stethoscope — doctors
  doc:     `<svg ${SVG_BASE}><path d="M6 3v6a4 4 0 0 0 8 0V3"/><path d="M6 3h2M14 3h-2M10 13v3a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4v-2"/><circle cx="18" cy="11" r="2"/></svg>`,
}

export default {
  name: 'WebsitesDashboard',
  data () {
    return {
      loading: true,
      errorMessage: '',
      counts: { products: 0, articles: 0, news: 0, divisions: 0, doctors: 0 },
      animated: { products: 0, articles: 0, news: 0, divisions: 0, doctors: 0 },
      today: this.formatToday(),
      greeting: this.computeGreeting(),
    }
  },
  computed: {
    cards () {
      return [
        { key: 'products',  label: 'Total Products',     hint: 'Items in catalog',   to: '/websites/products',  gradient: 'linear-gradient(135deg,#0e5b46 0%,#16a07a 60%,#1abc9c 100%)', icon: ICONS.bag },
        { key: 'articles',  label: 'Published Articles', hint: 'Blog posts live',    to: '/websites/articles',  gradient: 'linear-gradient(135deg,#4FA3D1 0%,#3b95c5 60%,#27a3d8 100%)', icon: ICONS.article },
        { key: 'news',      label: 'News Updates',       hint: 'Published updates',  to: '/websites/news',      gradient: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 60%,#8b5cf6 100%)', icon: ICONS.news },
        { key: 'divisions', label: 'Divisions',          hint: 'Contact divisions',  to: '/websites/divisions', gradient: 'linear-gradient(135deg,#059669 0%,#10b981 60%,#2dd4bf 100%)', icon: ICONS.org },
        { key: 'doctors',   label: 'Doctors',            hint: 'Team physicians',    to: '/websites/doctors',   gradient: 'linear-gradient(135deg,#e11d48 0%,#ec4899 60%,#fb7185 100%)', icon: ICONS.doc },
      ]
    },
    quickActions () {
      return [
        { key: 'p', title: 'Products',  desc: 'Catalog items',     to: '/websites/products',  tone: 'green' },
        { key: 'a', title: 'Articles',  desc: 'Editorial posts',   to: '/websites/articles',  tone: 'cyan' },
        { key: 'n', title: 'News',      desc: 'Press updates',     to: '/websites/news',      tone: 'indigo' },
        { key: 'd', title: 'Divisions', desc: 'Org contacts',      to: '/websites/divisions', tone: 'emerald' },
        { key: 'm', title: 'Doctors',   desc: 'Medical roster',    to: '/websites/doctors',   tone: 'rose' },
      ]
    },
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = await websitesApi.counts()
        this.counts = {
          products:  Number(data.products  || 0),
          articles:  Number(data.articles  || 0),
          news:      Number(data.news      || 0),
          divisions: Number(data.divisions || 0),
          doctors:   Number(data.doctors   || 0),
        }
        this.animateAll()
      } catch (e) {
        this.errorMessage = this.extractError(e)
      } finally {
        this.loading = false
      }
    },
    animateAll () {
      const duration = 800
      const start = performance.now()
      const from = { ...this.animated }
      const to   = { ...this.counts }
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration)
        const ease = 1 - Math.pow(1 - t, 3)
        const next = {}
        Object.keys(to).forEach(k => {
          next[k] = Math.round(from[k] + (to[k] - from[k]) * ease)
        })
        this.animated = next
        if (t < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    computeGreeting () {
      const h = new Date().getHours()
      if (h < 12) return 'Good Morning'
      if (h < 17) return 'Good Afternoon'
      return 'Good Evening'
    },
    formatToday () {
      const d = new Date()
      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).toUpperCase()
    },
    extractError (e) {
      const status = e && e.response && e.response.status
      if (status === 401) return 'Not authenticated. Please log in again.'
      if (status === 500) return 'Server error. Check the Neon database connection (MANAKKAL_DATABASE_URL).'
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) || 'Failed to load counts.'
    },
  },
}
</script>

<style scoped>
.ws-dash { display: flex; flex-direction: column; gap: 18px; }

/* Hero */
.ws-hero {
  position: relative;
  border-radius: 20px;
  padding: 28px 28px 30px;
  color: #fff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(22, 160, 122, 0.22);
}
.ws-hero__orb {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  pointer-events: none;
}
.ws-hero__orb--tr { width: 280px; height: 280px; top: -140px; right: -90px; animation: wsFloat 6s ease-in-out infinite; }
.ws-hero__orb--bl { width: 200px; height: 200px; bottom: -80px; left: -60px; animation: wsFloat 6s ease-in-out 2s infinite; }
.ws-hero__inner { position: relative; z-index: 1; }
.ws-hero__date {
  display: inline-block;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
}
.ws-hero__title { font-size: 1.7rem; font-weight: 700; margin: 0; }
.ws-hero__wave  { display: inline-block; animation: wsFloat 3s ease-in-out infinite; }
.ws-hero__sub   { color: rgba(255, 255, 255, 0.85); margin: 6px 0 14px; font-size: 0.9rem; max-width: 540px; }
.ws-hero__pills { display: flex; gap: 8px; flex-wrap: wrap; }
.ws-hero__pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.ws-hero__pill--green { color: #6ee7b7; }
.ws-hero__pill--blue  { color: #7dd3fc; }

/* Stat cards */
.ws-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
@media (min-width: 640px)  { .ws-stats { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1100px) { .ws-stats { grid-template-columns: repeat(5, 1fr); } }
.ws-stat {
  position: relative;
  border-radius: 16px;
  padding: 18px;
  color: #fff;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 130px;
  box-shadow: 0 6px 18px rgba(31, 47, 111, 0.14);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}
.ws-stat:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 12px 28px rgba(31, 47, 111, 0.22); color: #fff; }
.ws-stat__icon {
  width: 44px; height: 44px; border-radius: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.18);
  display: inline-flex; align-items: center; justify-content: center;
  margin-bottom: 6px;
  color: #fff;
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.22);
  transition: transform 0.25s ease, background 0.25s ease;
}
.ws-stat__icon svg { width: 16px; height: 16px; display: block; }
.ws-stat:hover .ws-stat__icon {
  background: rgba(255, 255, 255, 0.28);
  transform: rotate(-4deg) scale(1.05);
}
.ws-stat__count { font-size: 1.7rem; font-weight: 700; line-height: 1.1; }
.ws-stat__label { font-size: 0.85rem; font-weight: 600; }
.ws-stat__hint  { font-size: 0.72rem; color: rgba(255,255,255,0.78); }
.ws-stat__arrow {
  position: absolute; top: 14px; right: 14px;
  font-size: 1.05rem; opacity: 0.85;
  transition: transform 0.2s ease;
}
.ws-stat:hover .ws-stat__arrow { transform: translate(2px, -2px); }

/* Quick actions */
.ws-quick {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}
.ws-quick__head h3 { margin: 0; font-size: 1.05rem; color: #1f2a44; font-weight: 700; }
.ws-quick__head p  { margin: 2px 0 12px; font-size: 0.78rem; color: #6b7280; }
.ws-quick__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
@media (min-width: 640px)  { .ws-quick__grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1100px) { .ws-quick__grid { grid-template-columns: repeat(5, 1fr); } }
.ws-quick__card {
  display: flex; flex-direction: column; gap: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  text-decoration: none;
  border: 1px solid transparent;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.ws-quick__card:hover { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08); }
.ws-quick__title { font-weight: 600; font-size: 0.88rem; }
.ws-quick__desc  { font-size: 0.74rem; opacity: 0.75; }
.ws-quick__card--green   { background: #e6f4ef; color: #0e5b46; border-color: #b7e0cc; }
.ws-quick__card--cyan    { background: #e0f2fe; color: #0369a1; border-color: #bae6fd; }
.ws-quick__card--indigo  { background: #ede9fe; color: #5b21b6; border-color: #ddd6fe; }
.ws-quick__card--emerald { background: #ecfdf5; color: #047857; border-color: #d1fae5; }
.ws-quick__card--rose    { background: #fff1f2; color: #be123c; border-color: #ffe4e6; }

/* Footer */
.ws-foot {
  display: flex; justify-content: space-between;
  font-size: 0.74rem; color: #6b7280;
  padding: 6px 4px;
}
.ws-foot__dot {
  display: inline-block; width: 7px; height: 7px;
  border-radius: 50%; background: #10b981; margin-right: 6px;
  animation: wsPulse 2s infinite;
}

@keyframes wsFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes wsPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
</style>
